import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { CheckCircle2, Copy, Check, ExternalLink } from 'lucide-react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import Breadcrumb from '@/components/Breadcrumb'
import CTABanner from '@/components/CTABanner'
import MetaTags from '@/components/MetaTags'
import { Button } from '@/components/ui/button'

interface OrderPlacedState {
  checkoutUrl: string
  name: string
}

const OrderPlaced = () => {
  const [copied, setCopied] = useState(false)
  const location = useLocation()
  const state = location.state as OrderPlacedState | null

  const handleCopy = () => {
    if (state?.checkoutUrl) {
      navigator.clipboard.writeText(state.checkoutUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  if (!state?.checkoutUrl) {
    return (
      <>
        <MetaTags
          title="Pedido Não Encontrado - FotoLivro Store"
          description="Não encontramos informações sobre seu pedido"
          type="website"
        />

        <div className="min-h-screen flex flex-col">
          <Header />
          <Breadcrumb />
          <main
            className="flex-1"
            role="main"
            aria-label="Pedido não encontrado"
          >
            <div className="container mx-auto px-4 py-16 text-center">
              <h1 className="text-4xl font-bold text-foreground mb-4">
                Nenhuma Informação de Pedido
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Não encontramos informações sobre seu pedido. Por favor, retorne
                ao checkout.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild>
                  <a href="/">Voltar para Home</a>
                </Button>
                <Button variant="outline" asChild>
                  <a href="/checkout">Retornar ao Checkout</a>
                </Button>
              </div>
            </div>
          </main>
          <Footer />
        </div>
      </>
    )
  }

  return (
    <>
      <MetaTags
        title="Pedido Confirmado - FotoLivro Store"
        description={`Parabéns ${state.name}! Seu pedido foi confirmado e está sendo processado.`}
        type="website"
        keywords="pedido confirmado, pagamento fotolivro, finalização compra"
      />

      <div className="min-h-screen flex flex-col">
        <Header />
        <Breadcrumb />

        <main className="flex-1" role="main" aria-label="Confirmação de Pedido">
          <div className="bg-muted/30 py-12 md:py-16">
            <div className="container mx-auto px-4 max-w-3xl">
              {/* Main Card */}
              <div className="rounded-lg border bg-card text-card-foreground shadow-lg p-8 md:p-12 text-center">
                {/* Success Icon */}
                <div className="mx-auto flex items-center justify-center w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/20 mb-6">
                  <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-500" />
                </div>

                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Pedido Confirmado!
                </h1>

                {/* Message */}
                <div className="space-y-3 text-base md:text-lg text-muted-foreground mb-8">
                  <p>
                    Olá,{' '}
                    <span className="font-semibold text-foreground">
                      {state.name}
                    </span>
                    !
                  </p>
                  <p>
                    Seu pedido foi processado com sucesso e estamos preparando
                    tudo com muito carinho.
                  </p>
                  <p>
                    Em breve você receberá um e-mail com os detalhes e o
                    acompanhamento do seu pedido.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
                  <Button asChild size="lg">
                    <a
                      href={state.checkoutUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center"
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Acessar Pagamento
                    </a>
                  </Button>

                  <Button variant="outline" size="lg" asChild>
                    <a href="/" className="inline-flex items-center">
                      Voltar para Home
                    </a>
                  </Button>
                </div>

                {/* Copy Link */}
                <div className="pt-6 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-3">
                    Ou copie o link de pagamento:
                  </p>
                  <Button
                    variant="ghost"
                    onClick={handleCopy}
                    className="inline-flex items-center gap-2 text-primary"
                  >
                    {copied ? (
                      <>
                        <Check className="h-4 w-4" />
                        <span>Link copiado!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        <span>Copiar link de pagamento</span>
                      </>
                    )}
                  </Button>
                </div>

                {/* Additional Info */}
                <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-900 dark:text-blue-300">
                    💡 <strong>Dica:</strong> Guarde este link! Você pode usá-lo
                    para finalizar o pagamento mais tarde, caso necessário.
                  </p>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div className="flex flex-col items-center gap-2 p-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium">Pagamento Seguro</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium">Entrega Garantida</p>
                </div>
                <div className="flex flex-col items-center gap-2 p-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-primary" />
                  </div>
                  <p className="text-sm font-medium">Suporte Dedicado</p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional CTA */}
          <CTABanner
            title="Explore Mais Produtos"
            description="Enquanto seu pedido está sendo preparado, que tal conhecer outros produtos que podem complementar sua coleção de memórias?"
            buttonText="Ver Produtos"
            buttonLink="/#produtos"
          />
        </main>

        <Footer />
      </div>
    </>
  )
}

export default OrderPlaced
